import { createContext } from 'react';

/**
 * Runtime API configuration consumed by the Sidekick package's network-bound
 * hooks (`useSidekickEventStream`, `useSidekickSyncResponse`).
 *
 * `apiBaseUrl` is the absolute base URL of the Sidekick API service.
 * In `mf-insights`, the host passes
 * `${globalThis.appConfig.apiBaseUrl}/sidekick`. Hooks append the
 * conversation-scoped path (`/conversations/{id}/...`) themselves.
 */
export type SidekickApiContextValue = {
  apiBaseUrl: string;
  /** Optional fleet identifier applied to each host-originated message. */
  fleetId?: string;
  /** Optional host interface identifier used for backend experiment routing. */
  interfaceName?: string;
  ensureConversation?: EnsureSidekickConversation;
  submitFeedback?: SubmitSidekickFeedback;
  getConsentStatus?: GetSidekickConsentStatus;
  acceptConsent?: AcceptSidekickConsent;
  searchRecipients?: SearchSidekickRecipients;
};

/** Conversation identity returned after the host creates or reuses one. */
export type EnsuredSidekickConversation = {
  conversationId: string;
  createdAt: string;
};

/**
 * Callback that creates the backend conversation needed before the package
 * can send a user turn. It returns identity only and must not mutate chat
 * state; the orchestrator commits the result after validating its scope.
 */
export type EnsureSidekickConversation =
  () => Promise<EnsuredSidekickConversation>;

/** Feedback payload collected by the shared Sidekick feedback UI. */
export type SubmitSidekickFeedbackInput = {
  messageId: string;
  conversationId: string;
  traceId: string;
  rating: 'up' | 'down';
  comment?: string;
  category?: string;
};

/**
 * Callback that persists feedback for an assistant response.
 */
export type SubmitSidekickFeedback = (
  input: SubmitSidekickFeedbackInput
) => Promise<void>;

/**
 * Terms the backend reports as currently in force, mapped from the ECPP
 * CLTM `currentTerms` payload.
 */
export type SidekickConsentTerms = {
  /** Terms code, e.g. `AI_MONITORING_TC`. */
  code?: string;
  /** Terms revision, e.g. `20260617`. */
  revision?: string;
};

/**
 * Result of a consent-status lookup. `hasConsented` is `true` once the
 * current user has accepted the AI legal disclaimer (ECPP consent
 * `AI_INTERACTION_MONITORING`). The host maps the ECPP CLPT response
 * (or, pre-endpoint, the package's localStorage fallback) to this
 * shape; the gate only ever branches on `hasConsented`.
 */
export type SidekickConsentStatus = {
  hasConsented: boolean;
  /**
   * Terms in force at the time of the read. `useConsentApi` records
   * `revision` alongside a cached acceptance, so a later read reporting
   * a different revision replaces the cached record.
   */
  terms?: SidekickConsentTerms;
  /**
   * `true` when CLPT or CLTM was unreachable and the status is therefore
   * not authoritative. A degraded status is never cached — the next
   * entry re-reads instead of trusting a guess.
   */
  degraded?: boolean;
};

/**
 * Outcome of recording an acceptance. Reported so `useConsentApi` can
 * withhold the browser cache when the decision was only audited rather
 * than durably stored, which would otherwise let a user look consented
 * locally while the backend holds no record.
 */
export type SidekickConsentAcceptance = {
  /** `true` when the decision was durably written to CLPT. */
  persisted?: boolean;
  /** `true` when persistence was degraded. */
  degraded?: boolean;
};

/**
 * Callback that reads whether the current user has already accepted the AI
 * legal disclaimer. Rejects on transport failure — the
 * package surfaces the rejection as the "Something went wrong" error
 * toast and does NOT open the panel.
 *
 * Injected as a pair with {@link AcceptSidekickConsent}: the package
 * uses the injected callbacks only when BOTH are supplied. If only one
 * is provided it is ignored and both consent operations fall back to the
 * localStorage default (see `useConsentApi`), so the read and write
 * always share one source of truth.
 *
 * When the browser cache serves the entry, this is still called once in
 * the background to confirm what the cache claimed, and a `false` there
 * re-arms the consent gate mid-session. It must therefore reflect
 * current CLPT state rather than a per-session memo of its own.
 */
export type GetSidekickConsentStatus = () => Promise<SidekickConsentStatus>;

/**
 * Callback that records the current user's acceptance of the AI legal
 * disclaimer. Rejects on transport failure — the package keeps
 * the consent modal open and surfaces the error toast.
 *
 * Injected as a pair with {@link GetSidekickConsentStatus}: both must be
 * supplied together or both fall back to the localStorage default (see
 * that type for the all-or-nothing rule).
 *
 * Resolving with a {@link SidekickConsentAcceptance} is optional;
 * resolving `undefined` is read as "durably persisted", matching the
 * pre-ECPP localStorage default that cannot report otherwise.
 */
export type AcceptSidekickConsent = () => Promise<
  SidekickConsentAcceptance | undefined
>;

/**
 * One person the user can share an assistant response with.
 *
 * `displayName` is whatever the host's directory holds, and may be
 * absent for a profile that has none. It carries no signal about
 * where the recipient came from — the share field tracks that itself
 * on `ShareRecipient`, so a host cannot get it wrong.
 */
export type SidekickRecipient = {
  id: string;
  email: string;
  displayName?: string;
};

/**
 * Callback that resolves the host's user directory for a typed query.
 *
 * Left optional on {@link SidekickApiContextValue} because the package
 * owns no users client and has no default to fall back on: without a
 * host implementation the recipients field accepts free-typed email
 * addresses and shows no suggestions.
 *
 * The `signal` lets a caller abandon a request that a newer keystroke
 * has already superseded.
 */
export type SearchSidekickRecipients = (
  query: string,
  signal?: AbortSignal
) => Promise<SidekickRecipient[]>;

/**
 * Internal context shape. `null` represents "no provider on the
 * tree"; {@link useSidekickApi} throws on read so configuration
 * misses surface as a clear stack trace at integration time rather
 * than as silent network failures.
 *
 * Exported so test-only utilities and Storybook stories can override
 * the value directly; production code should always go through
 * {@link useSidekickApi}.
 */
export const SidekickApiContext = createContext<SidekickApiContextValue | null>(
  null
);
